/**
 * Flow Illustration
 *
 * Hand-drawn illustration of what this application does: figures in Xero
 * are pulled across a secured connection, turned into the quarter's
 * numbers, and handed to the person who has to make the call.
 *
 * House style: AIRUN-044 (Continuous-Line House Style as Hand-Authored
 * Inline SVG, status: draft — cited as guidance, not an Active standard).
 * Note AIRUN-042 governs AI-*generated* raster imagery and does not apply
 * to a hand-authored inline SVG in a component; 044 is its vector sibling.
 *
 * Where this follows the runbook, and where it deliberately does not:
 *
 *   Step 1 (single stroke + lift-off gap) — PARTIAL. The rule is written
 *     for a single glyph (a launcher bubble mark). A four-stage scene
 *     cannot be one path and stay legible, so what carries over is the
 *     tell, not the letter: the two containers lift off before closing.
 *   Step 2 (exactly ONE citrus accent) — followed. The accent is the
 *     target line, because revenue-against-target is this product's core
 *     loop. The circled total and the trend arrow were candidates and are
 *     demoted to base stroke colour per the step's own tie-break.
 *   Steps 5-6 (draw-on motion) — CONDITIONAL on an interactive host.
 *     This host is not interactive, so the hover/focus gating does not
 *     apply; the mount-time draw is kept inside the ADL motion budget and
 *     is fully disabled under prefers-reduced-motion (UX-AX-10).
 *   Step 7 (a11y hygiene) — followed. The scene restates the lede in
 *     pictures and carries no information of its own, so it is decoration:
 *     aria-hidden, focusable="false", no accessible name of its own.
 *
 * Strokes are deliberately irregular — control points are nudged off the
 * true line so the curves read as drawn rather than plotted.
 */

export function FlowIllustration() {
  return (
    <svg
      className="landing-art"
      viewBox="0 0 520 300"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* A little turbulence pushes every edge off true, so the strokes
            read as pen on paper rather than plotted vectors. */}
        <filter id="landing-art-ink" x="-6%" y="-6%" width="112%" height="112%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.019"
            numOctaves={2}
            seed={7}
            result="noise"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale={2.4}
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>

      <g className="landing-art__ink" filter="url(#landing-art-ink)">
        {/* ---- Ledger (source) ---------------------------------------- */}
        <g className="landing-art__stage landing-art__stage--1">
          {/* Lift-off gap: the left edge stops at y=96 rather than closing
              on the start point, the way a marker leaves the paper. */}
          <path
            pathLength={100}
            className="landing-art__stroke"
            d="M34 74 C33 70 36 67 40 67 L126 65 C131 65 133 68 133 72
               L136 210 C136 215 133 218 128 218 L44 220 C39 220 37 217 37 212
               L35 96"
          />
          {/* ruled lines, each a touch off-level */}
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__stroke--hair" d="M52 100 L118 98" />
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__stroke--hair" d="M52 124 L112 123" />
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__stroke--hair" d="M52 148 L120 146" />
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__stroke--hair" d="M52 172 L106 171" />
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__stroke--hair" d="M52 196 L114 194" />
          {/* The circled total. A candidate accent, demoted to base ink so
              the mark keeps exactly one citrus element (AIRUN-044 Step 2). */}
          <path
            pathLength={100}
            className="landing-art__stroke"
            d="M96 186 C112 182 128 188 126 197 C124 206 104 208 95 203 C88 199 89 189 99 187"
          />
        </g>

        {/* ---- The secured link --------------------------------------- */}
        <g className="landing-art__stage landing-art__stage--2">
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__flow"
            d="M146 142 C186 132 208 152 246 141"
          />
          <path
            pathLength={100}
            className="landing-art__stroke" d="M238 134 L248 141 L237 148" />
          {/* padlock — the connection is authenticated, which is the whole point */}
          <path
            pathLength={100}
            className="landing-art__stroke"
            d="M182 96 C182 84 200 84 200 96 L200 104"
          />
          <path
            pathLength={100}
            className="landing-art__stroke"
            d="M172 105 C171 102 173 100 176 100 L206 100 C209 100 211 102 210 105
               L209 124 C209 127 207 129 204 129 L177 129 C174 129 172 127 172 124 Z"
          />
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__stroke--hair" d="M191 110 L191 119" />
        </g>

        {/* ---- Dashboard (outcome) ------------------------------------ */}
        <g className="landing-art__stage landing-art__stage--3">
          {/* Lift-off gap at the top left, as on the ledger. */}
          <path
            pathLength={100}
            className="landing-art__stroke"
            d="M266 62 C265 57 268 54 273 54 L470 52 C475 52 478 55 478 60
               L481 206 C481 211 478 214 473 214 L276 216 C271 216 269 213 269 208
               L266 84"
          />
          {/* bars — the quarter, month by month */}
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__bar" d="M306 190 L305 150" />
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__bar" d="M340 191 L339 124" />
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__bar" d="M374 190 L373 138" />
          <path
            pathLength={100}
            className="landing-art__stroke landing-art__bar" d="M408 191 L407 104" />
          <path
            pathLength={100}
            className="landing-art__stroke" d="M292 192 C330 194 400 193 448 191" />
          {/* THE citrus accent (AIRUN-044 Step 2, one only): the target the
              bars are measured against — revenue-against-target is this
              product's core loop, so this is the narrative element.
              No pathLength: this path keeps a real dash pattern, and
              normalising would make 7-unit dashes swallow it. */}
          <path
            className="landing-art__stroke landing-art__accent landing-art__target"
            d="M292 118 C336 113 398 122 450 114"
          />
          {/* trend arrow — demoted to base ink with the circled total */}
          <path
            pathLength={100}
            className="landing-art__stroke"
            d="M430 96 L450 112 L440 90"
          />
        </g>

        {/* ---- The person making the call ----------------------------- */}
        <g className="landing-art__stage landing-art__stage--4">
          <path
            pathLength={100}
            className="landing-art__stroke"
            d="M372 246 C372 234 392 234 392 246 C392 257 372 257 372 246 Z"
          />
          <path
            pathLength={100}
            className="landing-art__stroke"
            d="M352 282 C355 264 371 258 382 258 C394 258 409 265 412 282"
          />
        </g>
      </g>
    </svg>
  );
}
